import React, { useEffect, useState, useRef } from 'react';

import * as Yup from 'yup';
import i18next from 'i18next';
import { useFormik } from 'formik';

import { Close } from '@mui/icons-material';
import { EditIcon } from '../../../../assets/images/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../Buttons/Button/Button';
import styles from './EditRoleBasedSkill.module.css';
import Subtitle from '../Subtitle/Subtitle';
import BasicPopup from '../../Popups/BasicPopup/BasicPopup';
import RoleBasedSkillsForm from '../../../Forms/Common/RoleBasedSkillsForm/RoleBasedSkillsForm';
import { useGetRoleBasedSkillsHook } from '../../../../utils/utilityHooks';
import {
    renderRoleSkills,
    processRoleBasedSkillData,
    handleAddRoleBasedSkills,
    handleOptionRemove,
    handleFormSubmit,
    handleDataAndLanguageChanges
} from './EditRoleBasedSkillHelpers';

function EditRoleBasedSkill() {
    // Redux state
    const user = useSelector(state => state.user.user);
    const userToken = useSelector(state => state.user.token);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const currentLanguage = i18next.language;

    // Local state
    const [formLoading, setFormLoading] = useState(false);
    const [editMode, setEditMode] = useState(true);
    const [isOpenRoleBasedSkillsPopup, setIsOpenRoleBasedSkillsPopup] = useState(false);
    const initialLoadDone = useRef(false);

    // Custom hook for options
    const options = useGetRoleBasedSkillsHook();

    // Simple popup handlers without dependencies
    const handlePopupClose = () => setIsOpenRoleBasedSkillsPopup(false);
    const handlePopupOpen = () => setIsOpenRoleBasedSkillsPopup(true);

    // Formik setup
    const formik = useFormik({
        initialValues: {
            roleBasedSkills: user.roleBasedSkills || [],
        },
        validationSchema: Yup.object({}),
        onSubmit: async (values) => {
            handleFormSubmit(values, userToken, dispatch, formik, setEditMode, setFormLoading, t);
        },
    });

    // Load initial data and update on language change
    useEffect(() => {
        handleDataAndLanguageChanges(options, user, initialLoadDone, formik, currentLanguage);
    }, [options, user, currentLanguage]);

    // Handler: Toggle edit mode and reset form if cancelled
    const handleEditToggle = () => {
        processRoleBasedSkillData(user.roleBasedSkills, user, options, formik, currentLanguage);
        setEditMode(prev => !prev);
    };

    // Modified handler function with proper parameters
    const handleOptionRemoveLocal = (roleSkillId) => {
        handleOptionRemove(roleSkillId, formik);
    };

    // Modified handler function with proper parameters
    const handleAddRoleBasedSkillsLocal = (values) => {
        handleAddRoleBasedSkills(values, options, formik, currentLanguage, handlePopupClose);
    };

    return (
        <div className={styles.container}>
            <div className={styles.secondColumn}>
                <div className={styles.title}>
                {t('general.UI.roleBasedSkills')}
                </div>
                {editMode ? (
                    <>
                        <div className={styles.aboutTitle}></div>
                        {renderRoleSkills(formik.values.roleBasedSkills, editMode, handleOptionRemoveLocal, styles, currentLanguage)}
                    </>
                ) : (
                    <div className={styles.editContainer}>
                        <div className={styles.aboutTitle}></div>
                        <Subtitle
                            dark
                            hasButton
                            buttonText={t('general.UI.add')}
                            buttonOnClick={handlePopupOpen}
                        >
                            {t('general.UI.roleBasedSkills')}
                        </Subtitle>
                        {renderRoleSkills(formik.values.roleBasedSkills, editMode, handleOptionRemoveLocal, styles, currentLanguage)}
                        <Button
                            onClick={formik.submitForm}
                            className={styles.saveButton}
                        >
                            {formLoading ? t('general.UI.loading') : t('general.UI.save')}
                        </Button>
                    </div>
                )}
            </div>

            {isOpenRoleBasedSkillsPopup && (
                <BasicPopup
                    isOpen={isOpenRoleBasedSkillsPopup}
                    closePopup={handlePopupClose}
                >
                    <RoleBasedSkillsForm
                        onSubmit={handleAddRoleBasedSkillsLocal}
                        excludeSkills={formik.values.roleBasedSkills.map(rs => rs.role_id || rs.role)}
                    />
                </BasicPopup>
            )}
            <div className={styles.thirdColumn}>
                {!editMode ? (
                    <Close
                        className={styles.crossIcon}
                        style={{ cursor: 'pointer' }}
                        onClick={handleEditToggle}
                    />
                ) : (
                    <img
                        src={EditIcon}
                        onClick={() => setEditMode(prev => !prev)}
                        alt={t('personalInformation.edit')}
                        className={styles.editIcon}
                    />
                )}
            </div>
        </div>
    );
}

export default EditRoleBasedSkill; 